import { collabStateManager } from '@databases/collab-state-manager';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { logError, logWarning } from '@databases/logs';
import type {
  AddFilterToken,
  AddHiddenFieldToken,
  EditEntryToken,
  EditFieldSettingToken,
  EditFilterToken,
  EditSortToken,
  EditViewSettingToken,
  FieldToken,
  LayoutToken,
  MetaToken,
  RemoveEntryToken,
  RemoveFieldToken,
  RemoveFilterToken,
  RemoveHiddenFieldToken,
  RemoveSortToken,
  RemoveViewToken,
  SortToken,
  Token,
  ValueToken,
  ViewToken,
} from '@databases/streaming-csv-parser';
import { DatabaseSortDirection, TokenType } from '@databases/streaming-csv-parser';
import {
  type DatabaseFieldSettings,
  DatabaseFieldType,
  DEFAULT_ENTRY_ID,
} from '@databases/types/database';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { createField } from '@databases/utils/database/utils';
import { createView } from '@databases/utils/database/view/view';
import { generateId } from '@databases/utils/utils';

import { type AgentLayoutSettingsOptions, DatabaseAgent } from './agents/database-agent';
import { FilterProcessor } from './filter-processor';
import type { ProcessingError } from './types';
import {
  ROW_RENDER_DELAY_MS,
  stringToDatabaseCardWidth,
  stringToDatabaseFieldType,
  stringToDatabaseLayoutType,
} from './utils';
import { ValueProcessor } from './value-processor';

const DEPENDENT_FIELD_TYPES = new Set([
  DatabaseFieldType.PAGE_DETAILS,
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.JIRA_ISSUE_DETAILS,
]);

/**
 * This class processes tokens emitted by the LLMParser and applies them to a y-database
 */
export class DatabasesAIFCProcessor {
  private agent: DatabaseAgent;
  private valueProcessor: ValueProcessor;
  private rowIdToEntryIdMappings: Map<string, string> = new Map();
  private fieldsSeen = new Set<string>();
  private cloudId: string;

  constructor(agent: DatabaseAgent, cloudId: string) {
    this.agent = agent;
    this.cloudId = cloudId;
    this.valueProcessor = new ValueProcessor(
      agent,
      this.onCurrentEntryIdChange.bind(this),
      this.onCurrentFieldIdChange.bind(this),
      this.onValueProcessed.bind(this)
    );
  }

  /**
   * Factory method to create processor from contentId
   * @param contentId - The database content ID
   * @returns DatabasesAIFCProcessor instance or null if database not found
   */
  static fromContentId(contentId: string): DatabasesAIFCProcessor | null {
    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!collabManager?.database) {
      return null;
    }

    const agent = new DatabaseAgent(collabManager.database);
    return new DatabasesAIFCProcessor(agent, collabManager.databaseAri.siteId);
  }

  public onToken(token: Token) {
    switch (token.tokenType) {
      case TokenType.FIELD:
        this.handleFieldToken(token);
        break;
      case TokenType.VIEW:
        // once we receive a view token, the fields are finished
        this.onFieldsComplete();
        this.handleViewToken(token);
        break;
      case TokenType.LAYOUT:
        this.handleLayoutToken(token);
        break;
      case TokenType.ADD_FILTER:
        this.handleFilterToken(token).catch((error) => {
          logError(error);
        });
        break;
      case TokenType.EDIT_FILTER:
        this.handleFilterToken(token).catch((error) => {
          logError(error);
        });
        break;
      case TokenType.SORT:
        this.handleSortToken(token);
        break;
      case TokenType.ADD_ENTRY:
        this.handleAddEntryToken();
        break;
      case TokenType.EDIT_ENTRY:
        this.handleEditEntryToken(token);
        break;
      case TokenType.REMOVE_ENTRY:
        this.handleRemoveEntryToken(token);
        break;
      case TokenType.VALUE:
        this.handleValueToken(token);
        break;
      case TokenType.META:
        this.handleMetaToken(token);
        break;
      case TokenType.EDIT_FIELD_SETTING:
        this.handleEditFieldSettingToken(token);
        break;
      case TokenType.EDIT_SORT:
        this.handleEditSortToken(token);
        break;
      case TokenType.REMOVE_SORT:
        this.handleRemoveSortToken(token);
        break;
      case TokenType.EDIT_VIEW_SETTING:
        this.handleEditViewSettingToken(token);
        break;
      case TokenType.REMOVE_FIELD:
        this.handleRemoveFieldToken(token);
        break;
      case TokenType.REMOVE_VIEW:
        this.handleRemoveViewToken(token);
        break;
      case TokenType.REMOVE_FILTER:
        this.handleRemoveFilterToken(token);
        break;
      case TokenType.ADD_HIDDEN_FIELD:
        this.handleAddHiddenFieldToken(token);
        break;
      case TokenType.REMOVE_HIDDEN_FIELD:
        this.handleRemoveHiddenFieldToken(token);
        break;
      default:
        throw new Error(`Unknown token type: ${token}`);
    }
  }

  public clearDatabase(): void {
    this.agent.clearDatabase();
  }

  public populateRowIdToEntryIdMappings(mappings: Map<string, string>) {
    this.rowIdToEntryIdMappings.clear();
    for (const [rowId, entryId] of mappings) {
      this.rowIdToEntryIdMappings.set(rowId, entryId);
    }
  }

  public onStreamingEnd(): void {
    this.valueProcessor.clearCurrentEntryId();
    this.valueProcessor.clearCurrentFieldId();
    this.resetIsSingleFieldStreaming();
    this.resetCurrentFieldOperation();
    this.resetCurrentEntryOperation();
  }

  /**
   * TODO: Error handling strategy
   *
   * For each validation error state, we have two potential approaches:
   * - Send back a "correction prompt" to convo-ai and re-parse the new output
   * - Continue processing and drop the invalid tokens (current approach)
   */

  private logValidationErrors(errors: ProcessingError[]): void {
    for (const error of errors) {
      if (error.severity === 'warning') {
        logWarning(new Error(error.message));
      } else {
        logError(new Error(error.message));
      }
    }
  }

  private onCurrentEntryIdChange(entryId: string | undefined) {
    this.updateCurrentlyProcessedEntryId(entryId);
  }

  private updateCurrentlyProcessedEntryId(entryId: string | undefined) {
    aiFirstCreationStreamingState.entryIdSelectedForProcessing = entryId || '';
  }

  private onCurrentFieldIdChange(fieldId: string | undefined) {
    this.updateCurrentlyProcessedFieldId(fieldId);
  }

  private updateCurrentlyProcessedFieldId(fieldId: string | undefined) {
    if (fieldId) {
      aiFirstCreationStreamingState.fieldIdSelectedForProcessing = fieldId;
    } else {
      setTimeout(() => {
        aiFirstCreationStreamingState.fieldIdSelectedForProcessing = '';
      }, ROW_RENDER_DELAY_MS);
    }
  }

  private handleFieldSeen(fieldId: string | undefined) {
    if (fieldId) {
      this.fieldsSeen.add(fieldId);
      if (this.fieldsSeen.size === 1) {
        aiFirstCreationStreamingState.isSingleFieldStreaming = true;
      } else if (this.fieldsSeen.size > 1) {
        aiFirstCreationStreamingState.isSingleFieldStreaming = false;
      }
    }
  }

  private resetIsSingleFieldStreaming() {
    setTimeout(() => {
      aiFirstCreationStreamingState.isSingleFieldStreaming = undefined;
    }, ROW_RENDER_DELAY_MS);
  }

  private resetCurrentEntryOperation() {
    aiFirstCreationStreamingState.currentEntryOperation = undefined;
  }

  private resetCurrentFieldOperation() {
    aiFirstCreationStreamingState.currentFieldOperation = undefined;
  }

  private onValueProcessed(fieldId: string, entryId: string): void {
    aiFirstCreationStreamingState.addProcessedValue(fieldId, entryId);
  }

  private onFieldsComplete() {
    aiFirstCreationStreamingState.fieldsAreFinishedStreaming = true;
  }

  private validateFieldName(
    fieldName: string,
    errors: ProcessingError[]
  ): string | undefined {
    const fieldId = this.agent.getFieldIdByName(fieldName);
    if (!fieldId) {
      errors.push({
        message: `Field ${fieldName} not found`,
      });
    }
    return fieldId;
  }

  private handleFieldToken(fieldToken: FieldToken) {
    const errors = this.validateField(fieldToken);
    const hasBlockingErrors = errors.some((error) => error.severity !== 'warning');

    this.logValidationErrors(errors);

    if (hasBlockingErrors) {
      return;
    }

    const fieldType = stringToDatabaseFieldType(fieldToken.type);
    if (!fieldType) {
      return;
    }

    const settings = this.buildFieldSettings(fieldType, fieldToken);
    const fieldData = { name: fieldToken.name || '' };

    const field = createField(fieldType, fieldData);
    this.updateCurrentlyProcessedFieldId(field.id);

    if (settings) {
      field.settings = { ...field.settings, ...settings };
    }
    this.handleFieldSeen(field.id);
    this.agent.addField(field);
  }

  private handleViewToken(viewToken: ViewToken) {
    const errors = this.validateView(viewToken);

    if (errors.length === 0) {
      const view = createView();
      view.name = viewToken.name;

      const isFirstView = this.agent.isViewsEmpty();

      this.agent.addView(view);

      if (isFirstView) {
        this.agent.setDefaultView(view.id);
      }
    } else {
      this.logValidationErrors(errors);
    }
  }

  // TODO: handle other layout settings
  private handleLayoutToken(layoutToken: LayoutToken) {
    const errors = this.validateLayout(layoutToken);
    const viewId = this.agent.getViewIdByName(layoutToken.viewName);

    if (errors.length > 0 || !viewId) {
      this.logValidationErrors(errors);
      return;
    }

    const settings: AgentLayoutSettingsOptions = {};
    const layoutType = stringToDatabaseLayoutType(layoutToken.type);

    if (layoutType) {
      settings.layoutType = layoutType;
    }

    if (layoutToken.groupField) {
      let fieldId = this.agent.getFieldIdByName(layoutToken.groupField);
      const existingField = fieldId ? this.agent.getFieldById(fieldId) : null;
      let fieldName = layoutToken.groupField;

      if (!fieldId) {
        // Field doesn't exist, create a new single-select field with the same name
        const field = createField(DatabaseFieldType.SELECT, {
          name: fieldName,
        });
        this.agent.addField(field);
        fieldId = field.id;
      } else if (existingField && existingField.type !== DatabaseFieldType.SELECT) {
        // Field exists but is not a tag field
        // First, try to find an existing single-select tag field
        const existingSingleSelectField = this.agent.findExistingSingleSelectField();

        if (existingSingleSelectField) {
          fieldId = existingSingleSelectField.id;
          fieldName = existingSingleSelectField.name;
        } else {
          // No existing single-select tag field found, create a new one with "1" appended
          fieldName = `${layoutToken.groupField} 1`;
          const field = createField(DatabaseFieldType.SELECT, {
            name: fieldName,
          });
          this.agent.addField(field);
          fieldId = field.id;
        }
      }

      // Ensure the field is single-select for board grouping
      this.agent.setFieldMultiple(fieldName, false);
      settings.groupFieldId = fieldId;
    }

    this.agent.updateViewLayout(viewId, settings);

    if (!aiFirstCreationStreamingState.firstLayoutProcessed) {
      aiFirstCreationStreamingState.firstLayoutProcessed = true;
    }
  }

  private async handleFilterToken(filterToken: AddFilterToken | EditFilterToken) {
    const errors = FilterProcessor.validateFilterToken(this.agent, filterToken);

    if (errors.length > 0) {
      this.logValidationErrors(errors);
      return;
    }

    const viewId = this.agent.getViewIdByName(filterToken.viewName);
    if (!viewId) {
      this.logValidationErrors([{ message: `View ${filterToken.viewName} not found` }]);
      return;
    }

    const filter = await FilterProcessor.convertFilterTokenToDatabaseFilter(
      this.agent,
      filterToken
    );

    if (!filter) {
      this.logValidationErrors([
        { message: 'No valid filters could be converted from the filter token' },
      ]);
      return;
    }

    this.agent.setViewFilter(viewId, filter);
  }

  private handleSortToken(sortToken: SortToken) {
    const errors = this.validateSort(sortToken);
    const viewId = this.agent.getViewIdByName(sortToken.viewName);
    const fieldId = this.validateFieldName(sortToken.fieldName, errors);

    if (errors.length > 0 || !viewId || !fieldId) {
      this.logValidationErrors(errors);
      return;
    }

    const ascending = sortToken.direction === DatabaseSortDirection.ASC;

    this.agent.addSortToView(viewId, fieldId, ascending);
  }

  private handleEditSortToken(editSortToken: EditSortToken) {
    const errors = this.validateEditSort(editSortToken);
    const viewId = this.agent.getViewIdByName(editSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(editSortToken.fieldName);

    if (errors.length > 0 || !viewId || !fieldId) {
      this.logValidationErrors(errors);
      return;
    }

    const ascending = editSortToken.direction === DatabaseSortDirection.ASC;

    this.agent.updateSort(viewId, fieldId, ascending);
  }

  private handleRemoveSortToken(removeSortToken: RemoveSortToken) {
    const errors = this.validateRemoveSort(removeSortToken);
    const viewId = this.agent.getViewIdByName(removeSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(removeSortToken.fieldName);

    if (errors.length > 0 || !viewId || !fieldId) {
      this.logValidationErrors(errors);
      return;
    }

    this.agent.removeSort(viewId, fieldId);
  }

  private handleRemoveViewToken(removeViewToken: RemoveViewToken) {
    const errors = this.validateRemoveView(removeViewToken);
    const viewId = this.agent.getViewIdByName(removeViewToken.viewName);

    if (errors.length > 0 || !viewId) {
      this.logValidationErrors(errors);
      return;
    }

    this.agent.deleteView(viewId);
  }

  private handleRemoveFilterToken(removeFilterToken: RemoveFilterToken) {
    const viewId = this.agent.getViewIdByName(removeFilterToken.viewName);

    if (!viewId) {
      this.logValidationErrors([
        {
          message: `View not found: ${removeFilterToken.viewName}. Cannot remove filter from non-existent view.`,
        },
      ]);
      return;
    }

    this.agent.clearViewFilter(viewId);
  }

  private handleAddHiddenFieldToken(hiddenFieldToken: AddHiddenFieldToken) {
    const errors = this.validateAddHiddenField(hiddenFieldToken);
    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);

    if (errors.length > 0 || !viewId) {
      this.logValidationErrors(errors);
      return;
    }

    const fieldIds = hiddenFieldToken.fieldNames
      .map((fieldName: string) => this.agent.getFieldIdByName(fieldName))
      .filter((fieldId: string | undefined): fieldId is string => !!fieldId);

    if (fieldIds.length > 0) {
      this.agent.hideFields(viewId, fieldIds);
    }
  }

  private handleRemoveHiddenFieldToken(hiddenFieldToken: RemoveHiddenFieldToken) {
    const errors = this.validateRemoveHiddenField(hiddenFieldToken);
    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);

    if (errors.length > 0 || !viewId) {
      this.logValidationErrors(errors);
      return;
    }

    const fieldIds = hiddenFieldToken.fieldNames
      .map((fieldName: string) => this.agent.getFieldIdByName(fieldName))
      .filter((fieldId: string | undefined): fieldId is string => !!fieldId);

    if (fieldIds.length > 0) {
      this.agent.showFields(viewId, fieldIds);
    }
  }

  // TODO: consider view settings when creating an entry. We may need to set default values.
  // https://product-fabric.atlassian.net/browse/CTD-5098
  private handleAddEntryToken() {
    const entry = {
      id: generateId(),
      values: {},
    };
    this.agent.addEntry(entry);
    this.valueProcessor.setCurrentEntryId(entry.id);
  }

  private handleEditEntryToken(editEntryToken: EditEntryToken) {
    const entryId = this.rowIdToEntryIdMappings.get(String(editEntryToken.rowNumber));

    if (!entryId) {
      return;
    }

    aiFirstCreationStreamingState.currentEntryOperation = 'edit';
    this.valueProcessor.setCurrentEntryId(entryId);
  }

  private handleRemoveEntryToken(removeEntryToken: RemoveEntryToken) {
    const entryId = this.rowIdToEntryIdMappings.get(String(removeEntryToken.rowNumber));

    if (!entryId) {
      return;
    }

    aiFirstCreationStreamingState.currentEntryOperation = 'delete';
    this.valueProcessor.setCurrentEntryId(entryId);

    setTimeout(() => {
      this.agent.removeEntry(entryId);
      this.valueProcessor.clearCurrentEntryId();
    }, ROW_RENDER_DELAY_MS);
  }

  private handleValueToken(valueToken: ValueToken) {
    this.valueProcessor.processValueToken(valueToken);
  }

  private handleMetaToken(metaToken: MetaToken) {
    const errors = this.validateMeta(metaToken);
    if (errors.length === 0) {
      // TODO handle meta token
      // make yjs mutation
    } else {
      // log error
    }
  }

  private handleEditFieldSettingToken(editFieldSettingsToken: EditFieldSettingToken) {
    const fieldId = this.agent.getFieldIdByName(editFieldSettingsToken.fieldName);
    if (fieldId) {
      this.updateCurrentlyProcessedFieldId(fieldId);
      this.handleFieldSeen(fieldId);
    }

    switch (editFieldSettingsToken.property) {
      case 'field_name':
        this.agent.setFieldName(
          editFieldSettingsToken.fieldName,
          String(editFieldSettingsToken.value)
        );
        break;
      case 'field_type': {
        const fieldType = stringToDatabaseFieldType(String(editFieldSettingsToken.value));
        if (!fieldType) {
          this.logValidationErrors([
            {
              message: `Unknown field type ${editFieldSettingsToken.value} for field ${editFieldSettingsToken.fieldName}`,
            },
          ]);
          return;
        }

        this.agent.setFieldType(editFieldSettingsToken.fieldName, fieldType);
        break;
      }
      case 'allow_multiple_values':
        this.agent.setFieldMultiple(
          editFieldSettingsToken.fieldName,
          editFieldSettingsToken.value === 'true' || editFieldSettingsToken.value === true
        );
        break;
      case 'field_description':
        this.agent.setFieldDescription(
          editFieldSettingsToken.fieldName,
          String(editFieldSettingsToken.value)
        );
        break;
      case 'default_value':
        this.agent.setDefaultEnabled(editFieldSettingsToken.fieldName, true);
        this.valueProcessor.setCurrentEntryId(DEFAULT_ENTRY_ID);
        this.valueProcessor.processValueToken({
          tokenType: TokenType.VALUE,
          fieldName: editFieldSettingsToken.fieldName,
          value: editFieldSettingsToken.value,
          resetValue: true,
        });
        break;
      case 'number_format': {
        const configValue = editFieldSettingsToken.value;

        if (
          typeof configValue === 'object' &&
          configValue &&
          'numberFormat' in configValue &&
          configValue.numberFormat
        ) {
          this.agent.setFieldNumberFormat(
            editFieldSettingsToken.fieldName,
            configValue.numberFormat
          );
        } else {
          this.logValidationErrors([
            {
              message: `Invalid number format configuration for field`,
            },
          ]);
        }
        break;
      }
      case 'depends_on': {
        const dependentFieldName = String(editFieldSettingsToken.value);
        if (!dependentFieldName) {
          this.logValidationErrors([
            {
              message: `Missing dependent field name for field ${editFieldSettingsToken.fieldName}`,
            },
          ]);
          return;
        }

        this.agent.setDependentFieldId(
          editFieldSettingsToken.fieldName,
          dependentFieldName
        );
        break;
      }
      default:
        this.logValidationErrors([
          {
            message: `Unknown property ${editFieldSettingsToken.property} for field ${editFieldSettingsToken.fieldName}`,
          },
        ]);
        break;
    }
  }

  private handleEditViewSettingToken(editViewSettingToken: EditViewSettingToken) {
    const errors = this.validateEditViewSetting(editViewSettingToken);
    const viewId = this.agent.getViewIdByName(editViewSettingToken.viewName);
    const value = String(editViewSettingToken.value);

    if (errors.length > 0 || !viewId) {
      this.logValidationErrors(errors);
      return;
    }

    switch (editViewSettingToken.property) {
      case 'layout': {
        const layoutType = stringToDatabaseLayoutType(value);
        this.agent.updateViewLayout(viewId, { layoutType });
        break;
      }
      case 'name': {
        this.agent.updateViewName(editViewSettingToken.viewName, value);
        break;
      }
      case 'numbered_entries': {
        this.agent.updateViewLayout(viewId, {
          numberedEntries: value === 'true',
        });
        break;
      }
      case 'card_size': {
        const cardWidth = stringToDatabaseCardWidth(value);
        this.agent.updateViewLayout(viewId, { cardWidth });
        break;
      }
      case 'display_field_names': {
        this.agent.updateViewLayout(viewId, { showFieldNames: value === 'true' });
        break;
      }
      case 'group_by_field': {
        const fieldId = this.agent.getFieldIdByName(value);
        this.agent.updateViewLayout(viewId, { groupFieldId: fieldId });
        break;
      }
      case 'view_position': {
        const position = parseInt(value, 10);
        this.agent.reorderView(viewId, position);
        break;
      }
      default:
        this.logValidationErrors([
          {
            message: `Unknown property ${editViewSettingToken.property} for view ${editViewSettingToken.viewName}`,
          },
        ]);
        break;
    }
  }

  private handleRemoveFieldToken(removeFieldToken: RemoveFieldToken) {
    const fieldId = this.agent.getFieldIdByName(removeFieldToken.fieldName);
    if (!fieldId) {
      this.logValidationErrors([
        {
          message: `Field not found: ${removeFieldToken.fieldName}. Cannot remove non-existent field.`,
        },
      ]);
      return;
    }

    aiFirstCreationStreamingState.fieldIdSelectedForProcessing = fieldId;
    aiFirstCreationStreamingState.currentFieldOperation = 'delete';

    setTimeout(() => {
      this.agent.removeField(fieldId);
      aiFirstCreationStreamingState.fieldIdSelectedForProcessing = '';
    }, ROW_RENDER_DELAY_MS);
  }

  private buildFieldSettings(
    fieldType: DatabaseFieldType,
    fieldToken: FieldToken
  ): Partial<DatabaseFieldSettings> | null {
    const settings: Partial<DatabaseFieldSettings> = {};

    if (fieldType === DatabaseFieldType.PAGE_LINK) {
      settings.creatable = false;
    }

    if (
      fieldType === DatabaseFieldType.PAGE_STATUS ||
      fieldType === DatabaseFieldType.PAGE_LABELS
    ) {
      settings.allowEdit = false;
    }

    if (fieldType === DatabaseFieldType.USER) {
      settings.mentionNotificationEnabled = false;
    }

    if (fieldType === DatabaseFieldType.JIRA_ISSUE) {
      settings.cloudId = this.cloudId;
    }

    if (fieldToken.dependsOn && DEPENDENT_FIELD_TYPES.has(fieldType)) {
      const dependentFieldId = this.agent.getFieldIdByName(fieldToken.dependsOn);
      if (dependentFieldId) {
        settings.fieldId = dependentFieldId;
      }
    }

    if (
      fieldToken.configuration?.numberFormat &&
      fieldType === DatabaseFieldType.NUMBER
    ) {
      settings.numberFormat = fieldToken.configuration.numberFormat;
    }

    return Object.keys(settings).length > 0 ? settings : null;
  }

  private validateField(fieldToken: FieldToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!fieldToken.name) {
      errors.push({
        message: 'Field name is missing',
      });
    }
    if (!fieldToken.type) {
      errors.push({
        message: `Field type for field ${fieldToken.name} is missing`,
      });
    }

    const fieldType = stringToDatabaseFieldType(fieldToken.type);
    if (!fieldType) {
      errors.push({
        message: `Unsupported field type ${fieldToken.type} for field ${fieldToken.name}`,
      });
      return errors;
    }

    if (fieldToken.dependsOn && DEPENDENT_FIELD_TYPES.has(fieldType)) {
      const dependentFieldId = this.agent.getFieldIdByName(fieldToken.dependsOn);
      if (!dependentFieldId) {
        errors.push({
          message: `Dependent field "${fieldToken.dependsOn}" not found for field "${fieldToken.name}". Field will be created without reference.`,
          severity: 'warning',
        });
      }
    }

    return errors;
  }

  private validateView(viewToken: ViewToken) {
    const errors: ProcessingError[] = [];

    if (!viewToken.name) {
      // TODO generate view name if missing
      // https://product-fabric.atlassian.net/browse/CNS-20319
    }

    // if (this.databaseAgent.getViewNames().has(viewToken.name)) {
    // TODO generate unique name if name already exists
    // https://product-fabric.atlassian.net/browse/CNS-20319
    // https://product-fabric.atlassian.net/browse/CTD-5064
    // }

    return errors;
  }

  private validateLayout(layoutToken: LayoutToken) {
    const errors: ProcessingError[] = [];

    if (!stringToDatabaseLayoutType(layoutToken.type)) {
      errors.push({
        message: `Unsupported layout type ${layoutToken.type}`,
      });
    }

    if (layoutToken.type === DatabaseLayoutType.BOARD) {
      if (!layoutToken.groupField) {
        errors.push({
          message: 'Board layout is missing a groupBy field',
        });
      }
    }

    return errors;
  }

  private validateSort(sortToken: SortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!sortToken.fieldName) {
      errors.push({
        message: 'Sort field name is missing',
      });
    }

    if (!sortToken.direction) {
      errors.push({
        message: `Sort direction is missing for field ${sortToken.fieldName}`,
      });
    }

    const viewId = this.agent.getViewIdByName(sortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(sortToken.fieldName);

    if (viewId && fieldId && this.agent.hasSortForField(viewId, fieldId)) {
      errors.push({
        message: `Sort already exists for field ${sortToken.fieldName} in view ${sortToken.viewName}`,
      });
    }

    return errors;
  }

  private validateEditSort(editSortToken: EditSortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!editSortToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    if (!editSortToken.fieldName) {
      errors.push({
        message: 'Field name is missing',
      });
    }

    if (!editSortToken.direction) {
      errors.push({
        message: `Sort direction is missing for field ${editSortToken.fieldName}`,
      });
    }

    const viewId = this.agent.getViewIdByName(editSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(editSortToken.fieldName);

    if (viewId && fieldId && !this.agent.hasSortForField(viewId, fieldId)) {
      errors.push({
        message: `No sort exists for field ${editSortToken.fieldName} in view ${editSortToken.viewName}. Cannot edit non-existent sort.`,
      });
    }

    return errors;
  }

  private validateRemoveSort(removeSortToken: RemoveSortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!removeSortToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    if (!removeSortToken.fieldName) {
      errors.push({
        message: 'Field name is missing',
      });
    }

    return errors;
  }

  private validateRemoveView(removeViewToken: RemoveViewToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!removeViewToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    // Check if this is the only view in the database
    if (this.agent.getViewCount() <= 1) {
      errors.push({
        message: 'Cannot delete the only remaining view in the database',
      });
    }

    return errors;
  }

  private validateEditViewSetting(
    editViewSettingToken: EditViewSettingToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!editViewSettingToken.viewName) {
      errors.push({
        message: 'View name is required',
      });
      return errors;
    }

    const viewId = this.agent.getViewIdByName(editViewSettingToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${editViewSettingToken.viewName}. Cannot edit non-existent view.`,
      });
      return errors;
    }

    const value = String(editViewSettingToken.value);

    switch (editViewSettingToken.property) {
      case 'layout': {
        if (!stringToDatabaseLayoutType(value)) {
          errors.push({
            message: `Invalid layout type: ${value} for view ${editViewSettingToken.viewName}`,
          });
        }
        break;
      }
      case 'name': {
        const existingViewId = this.agent.getViewIdByName(value);
        if (existingViewId && existingViewId !== viewId) {
          errors.push({
            message: `View with name ${value} already exists`,
          });
        }
        break;
      }
      case 'numbered_entries': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (layoutType !== DatabaseLayoutType.TABLE) {
          errors.push({
            message: `numbered_entries can only be set on table views`,
          });
        }

        if (value !== 'true' && value !== 'false') {
          errors.push({
            message: `numbered_entries value must be 'true' or 'false'`,
          });
        }
        break;
      }
      case 'card_size': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (
          layoutType !== DatabaseLayoutType.GALLERY &&
          layoutType !== DatabaseLayoutType.BOARD
        ) {
          errors.push({
            message: `card_size can only be set on gallery or board views`,
          });
        }

        if (!stringToDatabaseCardWidth(value)) {
          errors.push({
            message: `card_size value must be 'Small', 'Medium', or 'Large'`,
          });
        }
        break;
      }
      case 'display_field_names': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (
          layoutType !== DatabaseLayoutType.GALLERY &&
          layoutType !== DatabaseLayoutType.BOARD
        ) {
          errors.push({
            message: `display_field_names can only be set on gallery or board views`,
          });
        }

        if (value !== 'true' && value !== 'false') {
          errors.push({
            message: `display_field_names value must be 'true' or 'false'`,
          });
        }
        break;
      }
      case 'group_by_field': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (layoutType !== DatabaseLayoutType.BOARD) {
          errors.push({
            message: `group_by_field can only be set on board views`,
          });
        }

        const fieldId = this.agent.getFieldIdByName(value);
        if (!fieldId) {
          errors.push({
            message: `Field not found: ${value}`,
          });
        }
        break;
      }
      case 'view_position': {
        const position = parseInt(value, 10);
        if (isNaN(position) || position < 0) {
          errors.push({
            message: `view_position must be a non-negative integer (0-indexed)`,
          });
        }

        const totalViews = this.agent.getViewCount();
        if (position >= totalViews) {
          errors.push({
            message: `view_position ${position} is out of bounds. Valid range is 0-${
              totalViews - 1
            }.`,
          });
        }
        break;
      }
    }

    return errors;
  }

  private validateMeta(_metaToken: MetaToken) {
    const errors: ProcessingError[] = [];

    // TODO validate meta

    return errors;
  }

  private validateAddHiddenField(
    hiddenFieldToken: AddHiddenFieldToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!hiddenFieldToken.viewName) {
      errors.push({
        message: 'View name is required for add_hidden_field',
      });
    }

    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${hiddenFieldToken.viewName}. Cannot add hidden fields to non-existent view.`,
      });
    }

    for (const fieldName of hiddenFieldToken.fieldNames) {
      const fieldId = this.agent.getFieldIdByName(fieldName);
      if (!fieldId) {
        errors.push({
          message: `Field not found: ${fieldName}. Cannot hide non-existent field.`,
          severity: 'warning',
        });
      }
    }

    return errors;
  }

  private validateRemoveHiddenField(
    hiddenFieldToken: RemoveHiddenFieldToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!hiddenFieldToken.viewName) {
      errors.push({
        message: 'View name is required for remove_hidden_field',
      });
    }

    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${hiddenFieldToken.viewName}. Cannot remove hidden fields from non-existent view.`,
      });
    }

    for (const fieldName of hiddenFieldToken.fieldNames) {
      const fieldId = this.agent.getFieldIdByName(fieldName);
      if (!fieldId) {
        errors.push({
          message: `Field not found: ${fieldName}. Cannot show non-existent field.`,
          severity: 'warning',
        });
      }
    }

    return errors;
  }
}
